/**
 * Transforms media URLs to use authenticated backend proxy
 *
 * BEHAVIOR:
 * - Already proxied URLs (relative or absolute) → Add token
 * - S3 URLs (presigned or direct) → /api/media/:bucket/:key?token=JWT
 * - Other URLs → Return as-is with token
 *
 * EXAMPLES:
 * Input:  https://bringstomind.com/api/media/brings-to-mind-media/headshots/2/123.png
 * Output: https://bringstomind.com/api/media/brings-to-mind-media/headshots/2/123.png?token=xyz
 *
 * Input:  /api/media/btm-dev/reflections/1/video.webm
 * Output: /api/media/btm-dev/reflections/1/video.webm?token=xyz
 *
 * Input:  https://192.168.50.162:9000/btm-dev/reflections/1/video.webm?X-Amz-...
 * Output: /api/media/btm-dev/reflections/1/video.webm?token=xyz
 */
export function getAuthenticatedMediaUrl(url) {
  if (!url) return null;

  const token = localStorage.getItem('token');
  if (!token) {
    console.error('No authentication token found for media request');
    return url;
  }

  // Check if URL is already a relative proxy URL (starts with /api/media)
  if (url.startsWith('/api/media/')) {
    // Already proxied - just ensure token is present
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  }

  // Check if URL is an absolute URL that contains /api/media/ in pathname
  // This handles backend-generated URLs like: https://bringstomind.com/api/media/bucket/key
  try {
    const urlObj = new URL(url);
    if (urlObj.pathname.startsWith('/api/media/')) {
      // Already a proxy URL, just add token
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${encodeURIComponent(token)}`;
    }
  } catch (e) {
    // Not a valid absolute URL - continue to S3 transformation below
  }

  // Check if URL is an S3 URL (contains bucket name and key structure)
  // S3 URLs look like: https://HOST:PORT/BUCKET/KEY or https://HOST/BUCKET/KEY
  // Presigned URLs have query params: ?X-Amz-Algorithm=...
  try {
    const urlObj = new URL(url);

    // Check if this looks like an S3 URL
    // Common patterns:
    // - https://192.168.50.162:9000/btm-dev/reflections/...
    // - https://s3.amazonaws.com/bucket/key
    // - https://bucket.s3.amazonaws.com/key
    const pathname = urlObj.pathname;

    // Extract bucket and key from pathname
    // Path format: /BUCKET/KEY or /KEY (if bucket in subdomain)
    const pathParts = pathname.split('/').filter(p => p); // Remove empty strings

    if (pathParts.length >= 2) {
      // Assume first part is bucket, rest is key
      const bucket = pathParts[0];
      const key = pathParts.slice(1).join('/');

      // Transform to proxy URL: /api/media/BUCKET/KEY?token=JWT
      const proxyUrl = `/api/media/${bucket}/${key}`;
      return `${proxyUrl}?token=${encodeURIComponent(token)}`;
    }
  } catch (e) {
    // Not a valid URL - might be a relative path already
    console.warn('Could not parse media URL:', url, e);
  }

  // Fallback: just append token (for non-S3 URLs like blob:// or data://)
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}
